'use client';

import { LoadingDark, Typography } from '@musinsa/mds';
import type {
  GetRecruitmentCampaignsServiceResponse,
  RecruitmentSort,
} from '@musinsa/services-curator';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { curatorQueries } from '../../query-factory';
import { CampaignRecruitmentCard } from './CampaignRecruitmentCard';

interface Props {
  category?: string;
  likedBrandsOnly?: boolean;
  sort?: RecruitmentSort;
  columns?: '2' | '3';
}

const PAGE_SIZE = 30;

const GRID_COLS_CLASS = {
  '2': 'grid-cols-2',
  '3': 'grid-cols-3',
} as const;

export const CampaignRecruitmentGrid = ({
  category,
  likedBrandsOnly,
  sort,
  columns = '3',
}: Props) => {
  const queryParams = useMemo(
    () => ({
      categoryNo: category,
      likedBrandOnly: likedBrandsOnly,
      sort,
      size: PAGE_SIZE,
    }),
    [category, likedBrandsOnly, sort],
  );

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError } =
    useInfiniteQuery({
      ...curatorQueries.campaigns.recruitment(queryParams),
      initialPageParam: 1,
      getNextPageParam: (lastPage: GetRecruitmentCampaignsServiceResponse) =>
        lastPage.pagination.nextPage ?? undefined,
    });

  const items = useMemo(
    () => data?.pages.flatMap((page: GetRecruitmentCampaignsServiceResponse) => page.list) ?? [],
    [data],
  );

  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          fetchNextPage();
        }
      },
      { rootMargin: '600px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (isLoading && items.length === 0) {
    return (
      <div
        className="flex-1 flex items-center justify-center px-[16px]! py-[40px]!"
        role="status"
        aria-busy="true"
      >
        <LoadingDark size="size24x24" />
      </div>
    );
  }

  if (isError && items.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-[16px]! py-[40px]!">
        <Typography variant="body14pxReg" colorScheme="gray-500">
          캠페인 목록을 불러오지 못했어요.
        </Typography>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-[12px]! px-[16px]! py-[40px]!">
        <div className="flex flex-col items-center gap-[2px]! w-full">
          <Typography size="14" weight="medium">
            모집 중인 캠페인이 없습니다.
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`grid ${GRID_COLS_CLASS[columns]} pb-[24px]!`}>
        {items.map((item, index) => (
          <CampaignRecruitmentCard
            key={item.campaignId}
            campaignId={item.campaignId}
            index={index}
            brandId={item.brandId}
            goodsImage={item.goodsImage}
            brandName={item.brandName}
            goodsName={item.goodsName}
            goodsPrice={item.goodsPrice}
            recruitmentEndAt={item.recruitmentEndAt}
            snsChannels={item.snsChannels}
            extraGoodsCount={Math.max(0, item.goodsSlots.length - 1)}
          />
        ))}
      </div>
      <div ref={sentinelRef} className="h-[1px]" />
      {isFetchingNextPage && (
        <div
          className="w-full flex items-center justify-center py-[24px]!"
          role="status"
          aria-busy="true"
        >
          <LoadingDark size="size24x24" />
        </div>
      )}
      {/*
        하단 safe-area inset. grid 컨테이너에 직접 safe-area-bottom 을 걸면 `::after` 스페이서가
        grid item 으로 잡혀 마지막 카드 옆 셀로 끼어버린다(iOS). block spacer 로 분리해 항상
        리스트 맨 아래 전체 폭 인셋이 되도록 한다.
      */}
      <div className="safe-area-bottom" />
    </>
  );
};
